import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Settings, Plus, Edit, Trash2, Calendar, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaveConfigPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    default_quota: 12,
    carry_forward: false,
    max_carry_forward: 0,
    is_paid: true,
    requires_approval: true,
    min_days_notice: 1,
    is_active: true
  });

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hr/leave-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
      toast.error('Failed to load leave types');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingType) {
        await axios.put(`${API_URL}/api/hr/leave-types/${editingType.id}`, null, {
          params: formData,
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Leave type updated successfully');
      } else {
        await axios.post(`${API_URL}/api/hr/leave-types`, null, {
          params: formData,
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Leave type created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchLeaveTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save leave type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (leaveType) => {
    setEditingType(leaveType);
    setFormData({
      name: leaveType.name,
      code: leaveType.code,
      default_quota: leaveType.default_quota,
      carry_forward: leaveType.carry_forward || false,
      max_carry_forward: leaveType.max_carry_forward || 0,
      is_paid: leaveType.is_paid !== false,
      requires_approval: leaveType.requires_approval !== false,
      min_days_notice: leaveType.min_days_notice || 1,
      is_active: leaveType.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this leave type?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/hr/leave-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leave type deleted');
      fetchLeaveTypes();
    } catch (error) {
      toast.error('Failed to delete leave type');
    }
  };

  const resetForm = () => {
    setEditingType(null);
    setFormData({
      name: '',
      code: '',
      default_quota: 12,
      carry_forward: false,
      max_carry_forward: 0,
      is_paid: true,
      requires_approval: true,
      min_days_notice: 1,
      is_active: true
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Leave Configuration">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Leave Configuration">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Leave Types Configuration</h2>
            <p className="text-muted-foreground">Configure leave types and quotas for your organization</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-leave-type-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Leave Type
          </Button>
        </div>

        {/* Leave Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leaveTypes.map((type, idx) => (
            <motion.div
              key={type.id || idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className={!type.is_active ? 'opacity-60' : ''} data-testid={`leave-type-card-${type.code}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{type.name}</CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(type)} data-testid={`edit-${type.code}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(type.id)} data-testid={`delete-${type.code}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <CardDescription>Code: {type.code}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Default Quota</span>
                      <span className="font-semibold">{type.default_quota} days</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Min Notice</span>
                      <span className="font-semibold">{type.min_days_notice || 1} days</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-3">
                      <Badge variant={type.is_paid ? 'default' : 'secondary'}>
                        {type.is_paid ? 'Paid' : 'Unpaid'}
                      </Badge>
                      {type.carry_forward && (
                        <Badge variant="outline">Carry Forward ({type.max_carry_forward || 0})</Badge>
                      )}
                      <Badge variant={type.is_active ? 'default' : 'destructive'}>
                        {type.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {leaveTypes.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No leave types configured yet</p>
              <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Add First Leave Type
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingType ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Leave Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Casual Leave"
                    required
                    data-testid="leave-name-input"
                  />
                </div>
                <div>
                  <Label>Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toLowerCase()})}
                    placeholder="e.g., casual"
                    required
                    data-testid="leave-code-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Quota (days)</Label>
                  <Input
                    type="number"
                    value={formData.default_quota}
                    onChange={(e) => setFormData({...formData, default_quota: parseInt(e.target.value) || 0})}
                    min="0"
                    data-testid="quota-input"
                  />
                </div>
                <div>
                  <Label>Min Days Notice</Label>
                  <Input
                    type="number"
                    value={formData.min_days_notice}
                    onChange={(e) => setFormData({...formData, min_days_notice: parseInt(e.target.value) || 0})}
                    min="0"
                    data-testid="notice-days-input"
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Paid Leave</Label>
                  <Switch
                    checked={formData.is_paid}
                    onCheckedChange={(v) => setFormData({...formData, is_paid: v})}
                    data-testid="is-paid-switch"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Requires Approval</Label>
                  <Switch
                    checked={formData.requires_approval}
                    onCheckedChange={(v) => setFormData({...formData, requires_approval: v})}
                    data-testid="requires-approval-switch"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Carry Forward</Label>
                  <Switch
                    checked={formData.carry_forward}
                    onCheckedChange={(v) => setFormData({...formData, carry_forward: v})}
                    data-testid="carry-forward-switch"
                  />
                </div>
                
                {formData.carry_forward && (
                  <div>
                    <Label>Max Carry Forward (days)</Label>
                    <Input
                      type="number"
                      value={formData.max_carry_forward}
                      onChange={(e) => setFormData({...formData, max_carry_forward: parseInt(e.target.value) || 0})}
                      min="0"
                      data-testid="max-carry-forward-input"
                    />
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                    data-testid="is-active-switch"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="save-leave-type-btn">
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingType ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LeaveConfigPage;
